/**
 * Conversations Container Component
 * Container component with business logic for conversations
 */

import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useRouter } from 'next/router';
import { ConversationsPresenter } from './ConversationsPresenter';
import { useChat } from '@/hooks/useChat';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useChatScreenStore } from '@/store/chatScreenStore';
import { useSearchStore } from '@/store/searchStore';
import { useAuthTokens } from '@/utils/tokenHelper';
import { useRedirectStore } from '@/store/redirectStore';
import { useFeedbackStore } from '@/store/feedbackStore';
import { useConversationTabState } from '@/hooks/useConversationTabState';
import {
  findLocationByCardId,
  findLocationByPlaceName,
} from '../place-details/PlaceDetailsContainer';
import { MODE_CHIPS, TAB_ITEMS } from '@/constants/chatScreenData';
import { ChatResponse, ChatMessage, ChatLocation } from '@/types/chatTypes';
import { LocationPopup } from './LocationPopup';
import { toast } from 'sonner';
import { useEnrichedExperiences } from '@/hooks/useEnrichedExperiences';
import { SharePopup } from '@/components/common/SharePopup';
import { useShare } from '@/hooks/useShare';
import { saveChatState } from '@/utils/preLoginState';
import {
  markdownToPlainText,
  formatConversationForCopy,
} from '@/utils/markdownToPlainText';
import { isBusinessLocation } from '@/utils/locationTypeHelpers';

export interface ConversationsContainerProps {
  conversationId?: string; // ID of the conversation for independent state management
  initialQuery?: string | null;
  initialAudio?: string | null;
  initialResponse?: string | null;
  initialMessages?: ChatMessage[] | null;
  initialSessionId?: string | null;
  onCardClick?: (cardId: string) => void;
  onPlaceNameClick?: (placeName: string) => void;
  onPlaceSelect?: (location: ChatLocation | null) => void;
  onDataChange?: (
    messages: ChatMessage[],
    sessionId: string | null,
    isLoading: boolean
  ) => void;
  onClosePlaceDetails?: () => void;
}

export const ConversationsContainer: React.FC<ConversationsContainerProps> = ({
  conversationId,
  initialQuery,
  initialAudio,
  initialResponse,
  initialMessages: propInitialMessages,
  initialSessionId: propInitialSessionId,
  onCardClick: externalOnCardClick,
  onPlaceNameClick: externalOnPlaceNameClick,
  onPlaceSelect,
  onDataChange,
  onClosePlaceDetails,
}) => {
  // MOCK ERROR: Set to true to test error display UI
  const [mockError, setMockError] = useState<boolean>(false);
  const MOCK_ERROR_MESSAGE = 'Failed to send message. Please try again.';

  const router = useRouter();
  const scrollToTopRef = React.useRef<{
    scrollToTop: () => void;
    scrollContainerToTop: () => void;
  }>(null);

  // Helper function to scroll to top and ensure image grid is visible
  const scrollToTop = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    // Try to find the place details images grid and scroll it into view
    const findAndScrollToImageGrid = () => {
      // Method 1: Find the PlaceDetailsPresenter container first (has padding, borderRadius, and h2)
      const placeDetailsContainer = Array.from(
        document.querySelectorAll('div')
      ).find(el => {
        const styles = window.getComputedStyle(el);
        return (
          styles.padding &&
          styles.padding !== '0px' &&
          styles.borderRadius &&
          styles.borderRadius !== '0px' &&
          el.querySelector('h2') && // Has place name heading
          el.offsetHeight > 0
        );
      });

      // Method 2: Find the image grid - it's a div with grid layout inside PlaceDetailsPresenter
      // The grid has gridTemplateColumns: '1fr 1fr' and contains images
      let imageGrid = null;

      if (placeDetailsContainer) {
        // Find the div with grid layout that contains images (PlaceOverlayImageGrid)
        imageGrid = Array.from(
          placeDetailsContainer.querySelectorAll('div')
        ).find(el => {
          const styles = window.getComputedStyle(el);
          const hasGridLayout =
            styles.display === 'grid' &&
            (styles.gridTemplateColumns === '1fr 1fr' ||
              styles.gridTemplateColumns.includes('1fr'));
          const hasImages =
            el.querySelector('img') || el.querySelector('picture');
          return hasGridLayout && hasImages && el.offsetHeight > 0;
        });
      }

      // Method 3: Fallback - find any grid with images that's in the right panel area
      if (!imageGrid) {
        const rightPanel = Array.from(document.querySelectorAll('div')).find(
          el => {
            const styles = window.getComputedStyle(el);
            const maxWidth = styles.maxWidth;
            const hasClass =
              el.classList.contains('md:w-[618px]') ||
              el.classList.contains('md:max-w-[618px]');
            return (
              (maxWidth === '618px' || hasClass) &&
              el.offsetHeight > 0 &&
              el.offsetWidth > 0
            );
          }
        );

        if (rightPanel) {
          imageGrid = Array.from(rightPanel.querySelectorAll('div')).find(
            el => {
              const styles = window.getComputedStyle(el);
              const hasGridLayout =
                styles.display === 'grid' &&
                (styles.gridTemplateColumns === '1fr 1fr' ||
                  styles.gridTemplateColumns.includes('1fr'));
              const hasImages =
                el.querySelector('img') || el.querySelector('picture');
              return hasGridLayout && hasImages && el.offsetHeight > 0;
            }
          );
        }
      }

      // Priority 1: Scroll the image grid into view
      if (imageGrid) {
        try {
          imageGrid.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
          return true;
        } catch {
          // Silently fail
        }
      }

      // Priority 2: Scroll the PlaceDetailsPresenter container into view (shows header with close button)
      if (placeDetailsContainer) {
        try {
          placeDetailsContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
          return true;
        } catch {
          // Silently fail
        }
      }

      return false;
    };

    if (scrollTop > 0) {
      // First, try to scroll the image grid into view
      const scrolledToElement = findAndScrollToImageGrid();

      // Also scroll window to top as fallback
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        // Silently fail
      }

      // Also scroll after a delay to handle any layout shifts from opening place details
      requestAnimationFrame(() => {
        // Try scrolling to element again
        if (!scrolledToElement) {
          findAndScrollToImageGrid();
        }

        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Also try document.documentElement.scrollTo if available
          if (typeof document.documentElement.scrollTo === 'function') {
            document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch {
          // Silently fail
        }
      });

      // Additional scroll after a short delay to catch any late layout changes
      setTimeout(() => {
        const currentScroll =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0;

        // Try scrolling to element again
        findAndScrollToImageGrid();

        if (currentScroll > 0) {
          try {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } catch {
            // Silently fail
          }
        }
      }, 100);
    } else {
      // Even if at top, try to ensure image grid is visible
      findAndScrollToImageGrid();
    }
  };
  const [activeMode, setActiveMode] = useState<string>('');
  // Use per-conversation tab state hook
  const [activeTab, setActiveTab] = useConversationTabState(
    conversationId || propInitialSessionId || null,
    TAB_ITEMS[0]?.id || 'discover'
  );

  // Restore tab from URL if present (e.g., after login redirect)
  useEffect(() => {
    const tabFromUrl = router.query.tab as string | undefined;
    if (tabFromUrl && conversationId) {
      // Only restore if it's different from current tab
      if (tabFromUrl !== activeTab) {
        setActiveTab(tabFromUrl);
        console.log(
          '🔄 [ConversationsContainer] Restored tab from URL:',
          tabFromUrl
        );
      }
    }
  }, [router.query.tab, conversationId, activeTab, setActiveTab]);

  const [promptValue, setPromptValue] = useState<string>('');
  const [popupLocation, setPopupLocation] = useState<ChatLocation | null>(null);
  const {
    openSharePopup,
    shareUrl,
    popupOpen: sharePopupOpen,
    setPopupOpen: setSharePopupOpen,
    shareTitle,
    shareDescription,
  } = useShare({
    title: 'Share Chat',
    description: 'Copy the link below to share this conversation with others.',
  });
  const chatInputRef = useRef<HTMLDivElement>(null);
  const promptValueRef = useRef<string>('');

  // Use feedback store for like/dislike
  const { setFeedback, getFeedback } = useFeedbackStore();

  // Helper function to update both state and ref
  const updatePromptValue = (value: string) => {
    setPromptValue(value);
    promptValueRef.current = value;
  };

  // Parse initial response if available
  const initialMessages = useMemo(() => {
    if (!initialResponse) {
      return undefined;
    }

    try {
      const response: ChatResponse = JSON.parse(initialResponse);

      const userMessage: ChatMessage = {
        id: `msg_${Date.now()}_0`,
        role: 'user',
        content: initialQuery?.trim() || '',
        timestamp: new Date(),
        session_id: response.session_id,
      };

      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_1`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        session_id: response.session_id,
        locations: response.locations,
        followUpQuestions: response.followUpQuestions,
        mood: response.mood,
        intent: response.intent,
        overview: response.overview,
        experiences: response.experiences,
      };

      return [userMessage, assistantMessage];
    } catch {
      return undefined;
    }
  }, [initialResponse, initialQuery]);

  // Use the chat hook for API integration
  // Priority: propInitialSessionId > first message's session_id > null
  // This ensures loaded conversations maintain their session_id for all subsequent messages
  const chatOptions = useMemo(() => {
    const targetSessionId =
      propInitialSessionId ||
      propInitialMessages?.[0]?.session_id ||
      initialMessages?.[0]?.session_id ||
      null;

    const rawInitialMessages = propInitialMessages || initialMessages || [];
    let filteredInitialMessages = targetSessionId
      ? rawInitialMessages.filter(msg => msg.session_id === targetSessionId)
      : rawInitialMessages;

    // Safety: if we have messages but filter dropped all (e.g. session_id mismatch), use messages with targetSessionId so chat is never blank
    if (
      rawInitialMessages.length > 0 &&
      targetSessionId &&
      filteredInitialMessages.length === 0
    ) {
      filteredInitialMessages = rawInitialMessages.map(msg => ({
        ...msg,
        session_id: targetSessionId,
      }));
    }

    return {
      initialMessages: filteredInitialMessages,
      initialSessionId: targetSessionId,
    };
  }, [propInitialMessages, propInitialSessionId, initialMessages]);

  const {
    messages,
    isLoading,
    isLoadingResponse,
    isLoadingFollowUps,
    isLoadingExperiences,
    error,
    sendMessage,
    sessionId,
    retryLastMessage: originalRetryLastMessage,
  } = useChat(chatOptions);

  // Wrap retryLastMessage to also clear mock error
  const retryLastMessage = useCallback(() => {
    setMockError(false); // Clear mock error when retry is clicked
    originalRetryLastMessage();
  }, [originalRetryLastMessage]);

  // Log component initialization and message updates
  const prevLogRef = useRef<{
    conversationId: string | undefined;
    sessionId: string | null;
    messagesCount: number;
  } | null>(null);

  useEffect(() => {
    const shouldLog =
      !prevLogRef.current ||
      prevLogRef.current.conversationId !== conversationId ||
      prevLogRef.current.sessionId !== sessionId ||
      prevLogRef.current.messagesCount !== messages.length;

    if (shouldLog) {
      prevLogRef.current = {
        conversationId,
        sessionId,
        messagesCount: messages.length,
      };
    }
  }, [
    conversationId,
    sessionId,
    messages.length,
    propInitialMessages?.length,
    propInitialSessionId,
  ]);

  // Get chatScreenStore methods for saving conversations
  const {
    conversations,
    activeConversationId,
    createConversation,
    updateConversation,
    setActiveConversation,
    updateConversationId,
    getActiveConversation,
  } = useChatScreenStore();

  // Get search store for chat count tracking and login requirement
  const {
    incrementChatCount,
    isChatLoginRequired,
    chatCount,
    isLoggedIn: isLoggedInFromStore,
  } = useSearchStore();
  const { setRedirectUrl, setLoginContext } = useRedirectStore();

  // Check if user is logged in (tokens or store – store is set on callback before redirect so avoids double login prompt after redirect)
  const { accessToken, refreshToken } = useAuthTokens();
  const userIsLoggedIn =
    !!(accessToken && refreshToken) || !!isLoggedInFromStore;

  // Track previous messages to detect content changes (not just count)
  const previousMessagesRef = useRef<ChatMessage[]>([]);
  const previousSessionIdRef = useRef<string | null>(null);

  // Helper function to check if messages content has changed (including experiences and questions)
  const hasMessagesContentChanged = (
    prevMessages: ChatMessage[],
    currentMessages: ChatMessage[]
  ): boolean => {
    // If count changed, definitely changed
    if (prevMessages.length !== currentMessages.length) {
      return true;
    }

    // Compare each message for content changes, especially experiences and followUpQuestions
    for (let i = 0; i < currentMessages.length; i++) {
      const prevMsg = prevMessages[i];
      const currentMsg = currentMessages[i];

      if (!prevMsg) return true;

      // Check if experiences changed
      const prevExperiencesCount = prevMsg.experiences?.length || 0;
      const currentExperiencesCount = currentMsg.experiences?.length || 0;
      if (prevExperiencesCount !== currentExperiencesCount) {
        return true;
      }

      // Check if followUpQuestions changed
      const prevQuestionsCount = prevMsg.followUpQuestions?.length || 0;
      const currentQuestionsCount = currentMsg.followUpQuestions?.length || 0;
      if (prevQuestionsCount !== currentQuestionsCount) {
        return true;
      }

      // Check if other key fields changed
      if (
        prevMsg.content !== currentMsg.content ||
        prevMsg.locations?.length !== currentMsg.locations?.length ||
        prevMsg.overview !== currentMsg.overview
      ) {
        return true;
      }
    }

    return false;
  };

  // Save conversations to chatScreenStore when messages change (like results page saves search entries)
  useEffect(() => {
    // Only save if we have messages and a sessionId
    if (!messages.length || !sessionId) {
      return;
    }

    // Check if messages content has changed (including experiences and questions)
    const messagesChanged =
      hasMessagesContentChanged(previousMessagesRef.current, messages) ||
      sessionId !== previousSessionIdRef.current;

    if (!messagesChanged && previousSessionIdRef.current === sessionId) {
      return;
    }

    // Store previous sessionId before updating refs (needed for comparison)
    const previousSessionId = previousSessionIdRef.current;

    // Update refs with current messages (deep copy to track changes)
    previousMessagesRef.current = messages.map(msg => ({ ...msg }));
    previousSessionIdRef.current = sessionId;

    // Check if a conversation with this sessionId already exists
    const existingConversation = conversations.find(
      conv => conv.sessionId === sessionId || conv.id === sessionId
    );

    // CRITICAL FIX: If sessionId changed from API response and we have an active conversation,
    // update the conversation ID to match the new sessionId from the API
    const activeConversation = getActiveConversation();
    if (
      activeConversation &&
      activeConversation.id !== sessionId &&
      previousSessionId !== null &&
      previousSessionId !== sessionId &&
      !existingConversation
    ) {
      // Set flag before store/URL update so chat page can skip API fetch when it sees conversationId in URL
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('_internal_conversation_update', sessionId);
      }

      console.log(
        `🔄 [ConversationsContainer] Session ID changed from API: ${activeConversation.id} -> ${sessionId}. Updating conversation ID.`
      );
      updateConversationId(activeConversation.id, sessionId);

      // Update URL with new conversation ID (without page reload)
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            conversationId: sessionId,
          },
        },
        undefined,
        { shallow: true }
      );

      setTimeout(() => {
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('_internal_conversation_update');
        }
      }, 600);

      // After updating, the conversation should now exist with the new ID
      // Re-fetch the conversation after the update
      const updatedConversation = getActiveConversation();
      if (updatedConversation && updatedConversation.id === sessionId) {
        // Update the conversation with new messages
        updateConversation(updatedConversation.id, messages);
        return; // Early return since we've handled the update
      }
    }

    if (existingConversation) {
      // Update existing conversation with new messages
      updateConversation(existingConversation.id, messages);
      // Set it as active if it's not already
      if (activeConversationId !== existingConversation.id) {
        setActiveConversation(existingConversation.id);
      }
    } else {
      // Create new conversation with first user message
      const firstUserMessage = messages.find(m => m.role === 'user');
      if (firstUserMessage) {
        const newConversationId = createConversation(
          sessionId,
          firstUserMessage
        );
        // Update the conversation with all messages after creation
        // Use setTimeout to ensure the conversation is created first
        setTimeout(() => {
          updateConversation(newConversationId, messages);
        }, 0);
      }
    }
  }, [
    messages,
    sessionId,
    activeConversationId,
    conversations,
    createConversation,
    updateConversation,
    setActiveConversation,
    updateConversationId,
    getActiveConversation,
    router,
  ]);

  // Notify parent of messages, sessionId, and isLoading changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange(messages, sessionId, isLoading);
    }
  }, [messages, sessionId, isLoading, onDataChange]);

  // Use the audio recorder hook
  const {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
    error: recordingError,
    clearError: clearRecordingError,
    isActuallyRecording,
    cleanup: cleanupAudioRecorder,
  } = useAudioRecorder();

  // Cleanup audio recorder on unmount
  useEffect(() => {
    return () => {
      cleanupAudioRecorder();
    };
  }, [cleanupAudioRecorder]);

  // Don't populate input with initialQuery - it should only be used to send the message
  // The input should remain empty so users can type a new query

  // Track if initial query/audio has been sent (only if no pre-loaded response)
  const hasSentInitialMessage = useRef(false);

  // Auto-send initial query or audio when component mounts (only if no pre-loaded response)
  useEffect(() => {
    // If we have a pre-loaded response, messages are already set via useChat options
    if (initialResponse) {
      // Ensure input is cleared
      updatePromptValue('');
      return;
    }

    if (hasSentInitialMessage.current) return;

    if (initialQuery || initialAudio) {
      hasSentInitialMessage.current = true;

      const sendInitialMessage = async () => {
        try {
          if (initialAudio) {
            // Send audio message (empty string if no query provided)
            const messageText = initialQuery?.trim() || '';
            await sendMessage(messageText, initialAudio);
          } else if (initialQuery) {
            // Send text message
            await sendMessage(initialQuery);
          }
          // Ensure input is cleared after sending
          updatePromptValue('');
        } catch {
          hasSentInitialMessage.current = false; // Allow retry on error
        }
      };

      sendInitialMessage();
    } else {
      // Ensure input is cleared when there's no initial query/audio
      updatePromptValue('');
    }
  }, [initialQuery, initialAudio, initialResponse, sendMessage]);

  const handleModeChange = (modeId: string) => {
    setActiveMode(modeId);
  };

  const handleTabChange = (tabId: string) => {
    // Allow tab switching even when loading (only the active tab is disabled)
    setActiveTab(tabId);
    // Scroll to top when switching tabs
    // The scrollContainerToTop function already handles delays for tab content rendering
    if (scrollToTopRef.current?.scrollContainerToTop) {
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        scrollToTopRef.current?.scrollContainerToTop?.();
      });
    } else if (typeof window !== 'undefined') {
      // Fallback to window scroll if ref method is not available
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (typeof document.documentElement.scrollTo === 'function') {
          document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }
  };

  const handlePromptChange = (value: string) => {
    updatePromptValue(value);
  };

  const handlePromptSubmit = async () => {
    // Use ref to get the latest value immediately (avoids async state update issues)
    const currentValue = promptValueRef.current.trim();

    if (currentValue) {
      // Check if login is required after 2 chats (check both count and flag)
      // Block if count is already at or above 2, or if flag is set
      if (!userIsLoggedIn && (chatCount >= 2 || isChatLoginRequired)) {
        // Set redirect URL to current page and context to 'chat' for conversation restoration
        if (typeof window !== 'undefined') {
          const redirectPath =
            window.location.pathname + window.location.search;

          // Save chat state before redirect
          // Use API-returned sessionId (not client-generated conversationId)
          // The sessionId from useChat is the real session ID returned from API
          const apiSessionId = sessionId; // This is the session_id from API response

          console.log(
            '🔐 [ConversationsContainer] Login required - Setting redirect:',
            {
              redirectPath,
              apiSessionId: apiSessionId || 'none',
              clientConversationId: conversationId || 'none',
              activeConversationId: activeConversationId || 'none',
              activeTab,
              currentUrl: window.location.href,
              note: 'Using API sessionId, not client-generated conversationId',
            }
          );

          // Save chat state to localStorage for restoration after login
          // Use sessionId as both conversationId and sessionId since API uses session_id
          if (apiSessionId) {
            saveChatState(apiSessionId, apiSessionId, activeTab);
          }

          // Verify state was saved (for debugging)
          if (typeof window !== 'undefined' && apiSessionId) {
            try {
              const savedState = JSON.parse(
                localStorage.getItem('nexgo-pre-login-state') || '{}'
              );
              console.log(
                '✅ [ConversationsContainer] State save verification:',
                {
                  saved: !!savedState.chatState,
                  sessionId: savedState.chatState?.sessionId || 'missing',
                  conversationId:
                    savedState.chatState?.conversationId || 'missing',
                  activeTab: savedState.chatState?.activeTab || 'missing',
                  timestamp: savedState.timestamp || 'missing',
                  note: 'Using API sessionId as both conversationId and sessionId',
                }
              );
            } catch (e) {
              console.error(
                '❌ [ConversationsContainer] Failed to verify saved state:',
                e
              );
            }
          }

          // Build redirect URL with session context if available
          let finalRedirectPath = redirectPath;
          if (apiSessionId) {
            try {
              const url = new URL(redirectPath, window.location.origin);
              // Use sessionId for both conversationId and sessionId since API uses session_id
              url.searchParams.set('conversationId', apiSessionId);
              url.searchParams.set('sessionId', apiSessionId);
              if (activeTab) {
                url.searchParams.set('tab', activeTab);
              }
              finalRedirectPath = url.pathname + url.search;
            } catch {
              // If URL parsing fails, append query params manually
              const separator = redirectPath.includes('?') ? '&' : '?';
              finalRedirectPath = `${redirectPath}${separator}conversationId=${apiSessionId}&sessionId=${apiSessionId}${activeTab ? `&tab=${activeTab}` : ''}`;
            }
          }

          setRedirectUrl(finalRedirectPath);
          setLoginContext('chat');
          window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/google/v1/login`;
        }
        return;
      }

      // Switch to 'discover' tab if currently on other tabs (places, maps, experiences)
      if (activeTab !== 'discover') {
        setActiveTab('discover');
      }
      // Clear input immediately when user submits (before API call)
      updatePromptValue('');

      try {
        // Send message - sendMessage already waits for API response and adds assistant message
        await sendMessage(currentValue);

        // Single scroll after response arrives - debouncing is handled in the scroll function
        scrollToTopRef.current?.scrollToTop();

        // Increment chat count after successful send (only if not logged in)
        if (!userIsLoggedIn) {
          incrementChatCount();
        }
      } catch {
        // On error, restore the message so user can retry
        updatePromptValue(currentValue);
      }
    }
  };

  const handleMicClick = async () => {
    const actuallyRecording = isActuallyRecording();

    if (actuallyRecording) {
      const audioBase64 = await stopRecording();

      if (audioBase64) {
        // Check if login is required after 2 chats (check both count and flag)
        if (!userIsLoggedIn && (chatCount >= 2 || isChatLoginRequired)) {
          // Set redirect URL to current page and context to 'chat' for conversation restoration
          if (typeof window !== 'undefined') {
            setRedirectUrl(window.location.pathname + window.location.search);
            setLoginContext('chat');
            window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/google/v1/login`;
          }
          return;
        }

        // Switch to 'discover' tab if currently on other tabs
        if (activeTab !== 'discover') {
          setActiveTab('discover');
        }

        const messageText = promptValueRef.current.trim() || '';
        updatePromptValue('');

        try {
          await sendMessage(messageText, audioBase64);

          // Single scroll after response arrives - debouncing is handled in the scroll function
          scrollToTopRef.current?.scrollToTop();

          // Increment chat count after successful send (only if not logged in)
          if (!userIsLoggedIn) {
            incrementChatCount();
          }
        } catch {
          updatePromptValue(messageText);
        }
      } else {
        alert(
          'Failed to record audio. Please check your microphone permissions and try again.'
        );
      }
    } else {
      try {
        await startRecording();
      } catch {
        // Failed to start recording
      }
    }
  };

  const handleStopClick = async () => {
    if (isRecording) {
      const audioBase64 = await stopRecording();

      if (audioBase64) {
        // Check if login is required after 2 chats (check both count and flag)
        if (!userIsLoggedIn && (chatCount >= 2 || isChatLoginRequired)) {
          // Set redirect URL to current page and context to 'chat' for conversation restoration
          if (typeof window !== 'undefined') {
            setRedirectUrl(window.location.pathname + window.location.search);
            setLoginContext('chat');
            window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/google/v1/login`;
          }
          return;
        }

        // Switch to 'discover' tab if currently on other tabs
        if (activeTab !== 'discover') {
          setActiveTab('discover');
        }

        const messageText = promptValueRef.current.trim() || '';
        updatePromptValue('');

        try {
          await sendMessage(messageText, audioBase64);

          // Single scroll after response arrives - debouncing is handled in the scroll function
          scrollToTopRef.current?.scrollToTop();

          // Increment chat count after successful send (only if not logged in)
          if (!userIsLoggedIn) {
            incrementChatCount();
          }
        } catch {
          updatePromptValue(messageText);
        }
      } else {
        alert(
          'Failed to record audio. Please check your microphone permissions and try again.'
        );
      }
    }
  };

  const handleCardClick = (cardId: string) => {
    // First, check if it's an experience
    const clickedExperience = mapExperiences.find(exp => exp.id === cardId);
    if (clickedExperience) {
      // Navigate to experience detail page based on type
      if (clickedExperience.slug) {
        let route = '';
        switch (clickedExperience.type) {
          case 'package':
          case 'signature':
            route = `/package-details/${clickedExperience.slug}`;
            break;
          case 'activities':
            route = `/activity-details/${clickedExperience.slug}`;
            break;
          case 'riders-corner':
            route = `/riders-corner/${clickedExperience.id}`;
            break;
          case 'stays':
            route = `/stay-experience-details/${clickedExperience.id}`;
            break;
          default:
            route = `/package-details/${clickedExperience.slug}`;
        }
        router.push(route);
        return;
      }
    }

    // Find the location in messages that matches this card

    const clickedLocation = findLocationByCardId(messages, cardId);

    if (clickedLocation) {
      if (isBusinessLocation(clickedLocation)) {
        // Show popup for organization/business-only results
        setPopupLocation(clickedLocation);
        externalOnCardClick?.(cardId);
        return;
      }

      // Show full place details panel for tourist/place locations
      setPopupLocation(null);
      onPlaceSelect?.(clickedLocation);
      externalOnCardClick?.(cardId);

      // Scroll to top after a delay to ensure the right panel has rendered
      setTimeout(() => {
        scrollToTop();
      }, 50);

      // Also scroll after a longer delay to catch any late layout changes
      setTimeout(() => {
        scrollToTop();
      }, 300);

      return;
    }

    // Fallback: Find the location by cardId (which is the place_id)
    let placeId = cardId;

    // If cardId has an index suffix, extract the place_id
    const underscoreIndex = cardId.lastIndexOf('_');
    if (underscoreIndex > 0) {
      const afterUnderscore = cardId.substring(underscoreIndex + 1);
      if (/^\d+$/.test(afterUnderscore)) {
        placeId = cardId.substring(0, underscoreIndex);
      }
    }

    // Find location by place_id
    const fallbackLocation = locations.find(loc => loc.place_id === placeId);

    if (fallbackLocation) {
      const location = findLocationByPlaceName(
        messages,
        fallbackLocation.title || fallbackLocation.place
      );

      if (location) {
        if (isBusinessLocation(location)) {
          setPopupLocation(location);
          externalOnCardClick?.(cardId);
          return;
        }

        // Show full place details panel for tourist/place locations
        setPopupLocation(null);
        onPlaceSelect?.(location);
        externalOnCardClick?.(cardId);

        // Scroll to top after a delay to ensure the right panel has rendered
        setTimeout(() => {
          scrollToTop();
        }, 50);

        // Also scroll after a longer delay to catch any late layout changes
        setTimeout(() => {
          scrollToTop();
        }, 300);
      }
    }
  };

  const handleLike = (messageId: string) => {
    const currentFeedback = getFeedback(messageId);
    if (currentFeedback === 'like') {
      // Toggle off if already liked
      setFeedback(messageId, null);
    } else {
      // Set like and clear dislike
      setFeedback(messageId, 'like');
    }
  };

  const handleDislike = (messageId: string) => {
    const currentFeedback = getFeedback(messageId);
    if (currentFeedback === 'dislike') {
      // Toggle off if already disliked
      setFeedback(messageId, null);
    } else {
      // Set dislike and clear like
      setFeedback(messageId, 'dislike');
    }
  };

  const handleCopy = async (messageId?: string, content?: string) => {
    try {
      let textToCopy: string;

      if (content) {
        // If specific content is provided, convert it to plain text
        textToCopy = markdownToPlainText(content);
      } else {
        // If copying entire conversation, format it with role labels
        textToCopy = formatConversationForCopy(messages);
      }

      // Check if clipboard API is available
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        // improve copy message
        toast.success('Conversation copied to clipboard successfully');
        return;
      }

      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.fontSize = '16px'; // Prevent zoom on iOS
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        toast.success('Copied to clipboard');
      } else {
        throw new Error('Fallback copy failed');
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleShare = async (messageId?: string, content?: string) => {
    // Get the base URL - use production domain for sharing (matching activity-details og:url pattern)
    // All other pages use hardcoded 'https://nexgo.ai' for og:url, so we'll use the same pattern
    const getBaseUrl = () => {
      if (typeof window === 'undefined') {
        return 'https://nexgo.ai';
      }
      return window.location.origin;
    };

    // Construct the shareable chat link
    const chatId = conversationId || sessionId;
    const baseUrl = getBaseUrl();
    const url = chatId
      ? `${baseUrl}/chat?conversationId=${chatId}`
      : `${baseUrl}/chat`;

    // Open the share popup with the custom URL
    await openSharePopup(url);
  };

  const handleDownload = (messageId?: string, content?: string) => {
    const textToDownload = content || messages.map(m => m.content).join('\n');
    const blob = new Blob([textToDownload], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = messageId
      ? `chat-message-${messageId}.txt`
      : 'chat-messages.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePeopleAlsoAskClick = async (itemId: string, question: string) => {
    if (question) {
      const messageToSend = question.trim();

      try {
        // Close place details to trigger exit animation (slides out to the right)
        onClosePlaceDetails?.();

        // Switch to 'discover' tab if currently on other tabs
        if (activeTab !== 'discover') {
          setActiveTab('discover');
        }

        // Wait for place details exit animation to complete before sending message
        // This ensures smooth transition - exit animation is 300ms
        await new Promise(resolve => setTimeout(resolve, 350));

        // Send the message and wait for response
        await sendMessage(messageToSend);

        // Scroll after a delay to ensure place details is fully hidden
        // This prevents flickering and ensures smooth scroll
        setTimeout(() => {
          scrollToTopRef.current?.scrollToTop();
        }, 200);

        // Increment chat count after successful send (only if not logged in)
        // This is a chat API call, so it should follow the same flow as regular messages
        if (!userIsLoggedIn) {
          incrementChatCount();

          // Check if login is now required after incrementing (third successful call)
          // incrementChatCount automatically sets isChatLoginRequired when chatCount >= 2
          const updatedState = useSearchStore.getState();
          if (updatedState.isChatLoginRequired) {
            // Set redirect URL and login context
            // The login modal will be shown automatically by the chat page when isChatLoginRequired is true
            if (typeof window !== 'undefined') {
              const redirectPath =
                window.location.pathname + window.location.search;

              // Save chat state before redirect (same as handlePromptSubmit)
              // Use API-returned sessionId (not client-generated conversationId)
              // The sessionId from useChat is the real session ID returned from API
              const apiSessionId = sessionId; // This is the session_id from API response

              console.log(
                '🔐 [ConversationsContainer] Question click - Login required after successful API call:',
                {
                  redirectPath,
                  loginContext: 'chat',
                  chatCount: updatedState.chatCount,
                  apiSessionId: apiSessionId || 'none',
                  clientConversationId: conversationId || 'none',
                  activeTab,
                  note: 'Using API sessionId, not client-generated conversationId',
                }
              );

              // Save chat state to localStorage for restoration after login
              // Use sessionId as both conversationId and sessionId since API uses session_id
              if (apiSessionId) {
                saveChatState(apiSessionId, apiSessionId, activeTab);

                // Verify state was saved (for debugging)
                try {
                  const savedState = JSON.parse(
                    localStorage.getItem('nexgo-pre-login-state') || '{}'
                  );
                  console.log(
                    '✅ [ConversationsContainer] Question click - State save verification:',
                    {
                      saved: !!savedState.chatState,
                      sessionId: savedState.chatState?.sessionId || 'missing',
                      conversationId:
                        savedState.chatState?.conversationId || 'missing',
                      activeTab: savedState.chatState?.activeTab || 'missing',
                      timestamp: savedState.timestamp || 'missing',
                      note: 'Using API sessionId as both conversationId and sessionId',
                    }
                  );
                } catch (e) {
                  console.error(
                    '❌ [ConversationsContainer] Question click - Failed to verify saved state:',
                    e
                  );
                }

                // Build redirect URL with session context if available
                let finalRedirectPath = redirectPath;
                try {
                  const url = new URL(redirectPath, window.location.origin);
                  // Use sessionId for both conversationId and sessionId since API uses session_id
                  url.searchParams.set('conversationId', apiSessionId);
                  url.searchParams.set('sessionId', apiSessionId);
                  if (activeTab) {
                    url.searchParams.set('tab', activeTab);
                  }
                  finalRedirectPath = url.pathname + url.search;
                } catch {
                  // If URL parsing fails, append query params manually
                  const separator = redirectPath.includes('?') ? '&' : '?';
                  finalRedirectPath = `${redirectPath}${separator}conversationId=${apiSessionId}&sessionId=${apiSessionId}${activeTab ? `&tab=${activeTab}` : ''}`;
                }
                setRedirectUrl(finalRedirectPath);
              } else {
                setRedirectUrl(redirectPath);
              }

              setLoginContext('chat');
            }
          }
        }
      } catch {
        // Silently fail - don't increment count or trigger login on error
      }
    }
  };

  // Location/Place name click handler
  const handlePlaceNameClick = (placeName: string) => {
    console.log('🟠 [ConversationsContainer] 🖱️ Place name clicked:', {
      placeName,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n'),
    });

    let location = findLocationByPlaceName(messages, placeName);

    // If location not found, create a basic location object from the place name
    // This ensures the place details page can still open even if location data isn't in messages
    if (!location && placeName) {
      location = {
        place_id: '',
        place: placeName,
        title: placeName,
        place_name: placeName, // Ensure place_name is set for extractedLocation
        subtitle: '',
        image_urls: [],
        address: '',
        latitude: 0,
        longitude: 0,
        map_link: '',
        types: [],
        icon: '',
        viewport_northeast_lat: 0,
        viewport_northeast_lng: 0,
        viewport_southwest_lat: 0,
        viewport_southwest_lng: 0,
        locality: '',
        administrative_area_level_3: '',
        administrative_area_level_1: '',
        country: '',
        postal_code: '',
      };
    } else if (location && placeName) {
      // Always create a new object to ensure React detects the change
      // This ensures every click triggers a new API call, even for the same place
      // Type for location with optional _clickTimestamp
      type LocationWithTimestamp = ChatLocation & {
        _clickTimestamp?: number;
      };
      location = {
        ...location,
        place_name: placeName, // Always use the clicked place name
        // Also update place and title if they're missing
        place: location.place || placeName,
        title: location.title || placeName,
        // Add a timestamp to ensure the object reference is always different
        // This forces React to detect the change and trigger re-renders
        _clickTimestamp: Date.now(),
      } as LocationWithTimestamp;
    }

    if (location) {
      // For organization/business mentions in response text,
      // open the same popup used by Places cards.
      if (isBusinessLocation(location)) {
        setPopupLocation(location);
        externalOnPlaceNameClick?.(placeName);
        return;
      }

      // Type for location with optional _clickTimestamp
      type LocationWithTimestamp = ChatLocation & {
        _clickTimestamp?: number;
      };
      const locationWithTimestamp = location as LocationWithTimestamp;
      console.log('🟠 [ConversationsContainer] Location found/created:', {
        title: location.title,
        place: location.place,
        place_name: location.place_name,
        place_id: location.place_id,
        latitude: location.latitude,
        longitude: location.longitude,
        clickTimestamp: locationWithTimestamp._clickTimestamp,
        timestamp: new Date().toISOString(),
      });

      // Show full place details panel for all locations
      console.log('🟠 [ConversationsContainer] 🚀 Calling onPlaceSelect:', {
        locationTitle: location.title,
        locationPlaceName: location.place_name,
        timestamp: new Date().toISOString(),
      });
      onPlaceSelect?.(location);
      console.log(
        '🟠 [ConversationsContainer] ✅ onPlaceSelect called, now calling externalOnPlaceNameClick'
      );
      externalOnPlaceNameClick?.(placeName);

      // Scroll to top after a delay to ensure the right panel has rendered
      // This ensures the close button at the top is visible
      setTimeout(() => {
        scrollToTop();
      }, 50);

      // Also scroll after a longer delay to catch any late layout changes
      setTimeout(() => {
        scrollToTop();
      }, 300);
    } else {
      console.warn(
        '[ConversationsContainer] No location found for place name:',
        placeName
      );
    }
  };

  // Get raw locations from API (filtering moved to components)
  const locations = useMemo((): ChatLocation[] => {
    // CRITICAL FIX: Always filter by sessionId to ensure isolation
    const targetSessionId = propInitialSessionId || sessionId;
    const currentConversationMessages = targetSessionId
      ? messages.filter(msg => msg.session_id === targetSessionId)
      : messages;

    const latestMessage = currentConversationMessages
      .slice()
      .reverse()
      .find(
        msg =>
          msg.role === 'assistant' && msg.locations && msg.locations.length > 0
      );

    return latestMessage?.locations || [];
  }, [messages, sessionId, propInitialSessionId]);

  // Get dynamic overview data from the latest assistant message
  const overviewData = useMemo(() => {
    // CRITICAL FIX: Always filter by sessionId to ensure isolation
    const targetSessionId = propInitialSessionId || sessionId;
    const currentConversationMessages = targetSessionId
      ? messages.filter(msg => msg.session_id === targetSessionId)
      : messages;

    const latestMessage = currentConversationMessages
      .slice()
      .reverse()
      .find(msg => msg.role === 'assistant' && (msg.overview || msg.content));

    if (!latestMessage) {
      return {
        title: 'Quick Overview',
        description:
          "Ask me anything about travel destinations, activities, and experiences. I'm here to help you plan your perfect trip!",
      };
    }

    // Use overview field if available, otherwise fallback to truncated content
    const description =
      latestMessage.overview ||
      latestMessage.content.substring(0, 300) +
        (latestMessage.content.length > 300 ? '...' : '');

    return {
      title: 'Quick Overview',
      description,
    };
  }, [messages, sessionId, propInitialSessionId]);

  // Get follow-up questions from the latest assistant message
  const followUpQuestions = useMemo(() => {
    // CRITICAL FIX: Always filter by sessionId to ensure isolation
    const targetSessionId = propInitialSessionId || sessionId;
    const currentConversationMessages = targetSessionId
      ? messages.filter(msg => msg.session_id === targetSessionId)
      : messages;

    const latestMessage = currentConversationMessages
      .slice()
      .reverse()
      .find(
        msg =>
          msg.role === 'assistant' &&
          msg.followUpQuestions &&
          msg.followUpQuestions.length > 0
      );

    // Don't return dummy questions - only return questions if API provides them
    if (
      !latestMessage?.followUpQuestions ||
      latestMessage.followUpQuestions.length === 0
    ) {
      return [];
    }

    return latestMessage.followUpQuestions.map((question, index) => ({
      id: `followup_${index}`,
      question,
    }));
  }, [messages, sessionId, propInitialSessionId]);

  // Get intent and mood from the latest assistant message (last chat)
  // This ensures when loading from history, the last chat's intent and mood are displayed
  const latestResponseMeta = useMemo(() => {
    // CRITICAL FIX: Always filter by sessionId to ensure isolation
    const targetSessionId = propInitialSessionId || sessionId;
    const currentConversationMessages = targetSessionId
      ? messages.filter(msg => msg.session_id === targetSessionId)
      : messages;

    // Find the last assistant message in the filtered messages array
    // This will be the most recent chat response for this conversation
    let latestMessage: ChatMessage | undefined;
    for (let i = currentConversationMessages.length - 1; i >= 0; i--) {
      if (currentConversationMessages[i].role === 'assistant') {
        latestMessage = currentConversationMessages[i];
        break;
      }
    }

    return {
      intent: latestMessage?.intent,
      mood: latestMessage?.mood,
    };
  }, [messages, sessionId, propInitialSessionId]);

  // Get raw experiences from latest message
  const rawExperiences = useMemo(() => {
    // CRITICAL FIX: Always filter by sessionId to ensure isolation
    // Even when conversationId is provided, the messages array from useChat
    // might contain messages from multiple sessions, so we must filter
    const targetSessionId = propInitialSessionId || sessionId;
    const currentConversationMessages = targetSessionId
      ? messages.filter(msg => msg.session_id === targetSessionId)
      : messages;

    const latestMessage = currentConversationMessages
      .slice()
      .reverse()
      .find(
        msg =>
          msg.role === 'assistant' &&
          msg.experiences &&
          msg.experiences.length > 0
      );

    return latestMessage?.experiences || [];
  }, [messages, sessionId, propInitialSessionId]);

  // Enrich experiences with media (fetches media for stay experiences)
  const mapExperiences = useEnrichedExperiences(rawExperiences);

  // CRITICAL FIX: Don't filter tabs at container level based on ALL messages
  // Each response pair should filter tabs based on its own content to ensure isolation
  // Passing all tabs and letting each ConversationResponsePair filter based on its own experiences/locations
  // This prevents tabs from being shared between different chats
  const filteredTabs = TAB_ITEMS;

  // Note: Tab switching logic is now handled per response pair in ConversationResponsePair
  // This ensures each chat conversation has isolated tab state
  // Container-level tab switching based on ALL messages is removed to prevent cross-chat contamination

  return (
    <>
      <ConversationsPresenter
        modeChips={MODE_CHIPS}
        activeMode={activeMode}
        onModeChange={handleModeChange}
        promptValue={promptValue}
        onPromptChange={handlePromptChange}
        onPromptSubmit={handlePromptSubmit}
        tabs={filteredTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        intent={latestResponseMeta.intent}
        mood={latestResponseMeta.mood}
        overviewDescription={overviewData.description}
        onCardClick={handleCardClick}
        onExperienceClick={handleCardClick}
        messages={messages}
        onLike={handleLike}
        onDislike={handleDislike}
        onCopy={handleCopy}
        onShare={handleShare}
        onDownload={handleDownload}
        peopleAlsoAsk={followUpQuestions}
        onPeopleAlsoAskClick={handlePeopleAlsoAskClick}
        onPlaceNameClick={handlePlaceNameClick}
        isLoading={isLoading}
        isLoadingResponse={isLoadingResponse}
        isLoadingFollowUps={isLoadingFollowUps}
        isLoadingExperiences={isLoadingExperiences}
        error={mockError ? MOCK_ERROR_MESSAGE : error || recordingError || null}
        onRetry={retryLastMessage}
        onErrorDismiss={() => {
          setMockError(false);
          clearRecordingError();
        }}
        onMicClick={handleMicClick}
        onStopClick={handleStopClick}
        isRecording={isRecording}
        recordingTime={recordingTime}
        chatInputRef={chatInputRef}
        scrollToTopRef={scrollToTopRef}
      />

      {/* Business Location Popup */}
      {popupLocation && (
        <LocationPopup
          location={popupLocation}
          onClose={() => {
            setPopupLocation(null);
            // Optionally clear place details when popup closes
            // onPlaceSelect?.(null as any);
          }}
        />
      )}

      {/* Share Popup */}
      <SharePopup
        open={sharePopupOpen}
        onOpenChange={setSharePopupOpen}
        shareUrl={shareUrl}
        title={shareTitle}
        description={shareDescription}
      />
    </>
  );
};
